"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import {
  Paper,
  Stack,
  Group,
  Text,
  SegmentedControl,
  Select,
  BiLayer,
  TbCalendar,
} from "@forestsync/ui";
import { ColorLegend } from "@/components/map/ColorLegend";
import { GeoTiffViewer } from "@/components/map/GeoTiffViewer";
import { ImageryComparison } from "@/components/map/ImageryComparison";

interface ImageryData {
  year: number;
  biomassUrl?: string | null;
  biomassChangeUrl?: string | null;
  sentinelUrl?: string | null;
  uploadedAt?: Date;
  metadata?: any;
}

interface ImagerySectionProps {
  imagery: ImageryData[];
}

/**
 * ImagerySection component for displaying satellite imagery layers
 *
 * Features:
 * - Simplified full-width layout
 * - Top toolbar for controls
 * - No overlay mode
 */
export function ImagerySection({
  imagery,
}: ImagerySectionProps) {
  const t = useTranslations("sink.imagery");
  const [selectedYear, setSelectedYear] = useState(
    imagery.length > 0
      ? imagery[imagery.length - 1].year
      : new Date().getFullYear(),
  );

  // Active layer
  const [activeLayer, setActiveLayer] = useState<
    "biomass" | "biomass-change" | "sentinel"
  >("biomass");

  // View mode: single or comparison
  const [viewMode, setViewMode] = useState<
    "single" | "compare-years" | "compare-layers"
  >("single");

  // Comparison state
  const [compareYear, setCompareYear] = useState<number>(
    imagery.length > 1 ? imagery[imagery.length - 2].year : selectedYear,
  );
  const [compareLayer, setCompareLayer] = useState<
    "biomass" | "biomass-change" | "sentinel"
  >("biomass-change");

  // Get available years
  const availableYears = imagery.map((img) => img.year);

  // Get current year imagery
  const currentImagery = imagery.find((img) => img.year === selectedYear);

  // Handle Layer Change
  const handleLayerChange = (value: string) => {
    const layer = value as "biomass" | "biomass-change" | "sentinel";
    setActiveLayer(layer);
  };

  // Empty state
  if (imagery.length === 0) {
    return (
      <Paper withBorder p="lg" radius="md">
        <Stack gap="md">
          <Group justify="space-between">
            <div>
              <Text size="lg" fw={600}>
                {t("title")}
              </Text>
              <Text size="sm" c="dimmed">
                {t("description")}
              </Text>
            </div>
          </Group>
          <Text size="sm" c="dimmed" className="text-center py-8">
            {t("noImageryYet")}
          </Text>
        </Stack>
      </Paper>
    );
  }

  const layerOptions = [
    {
      value: "biomass",
      label: t("biomassDensity"),
      disabled: !currentImagery?.biomassUrl,
    },
    {
      value: "biomass-change",
      label: t("biomassChange"),
      disabled: !currentImagery?.biomassChangeUrl,
    },
    {
      value: "sentinel",
      label: t("sentinel"),
      disabled: !currentImagery?.sentinelUrl,
    },
  ];

  return (
    <Paper withBorder p="lg" radius="md">
      <Stack gap="md">
        {/* Header & Controls Toolbar */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Text size="lg" fw={600}>
              {t("title")}
            </Text>
            <Text size="sm" c="dimmed" className="max-w-2xl">
              {t("description")}
            </Text>
          </div>

          <Group gap="sm" className="flex-wrap">
            {/* View Mode */}
            <SegmentedControl
              value={viewMode}
              onChange={(value) => setViewMode(value as typeof viewMode)}
              data={[
                { label: t("single"), value: "single" },
                { label: t("compareYears"), value: "compare-years" },
                { label: t("compareLayers"), value: "compare-layers" },
              ]}
              size="sm"
            />
          </Group>
        </div>

        {/* Filters Toolbar */}
        <Paper p="sm" withBorder className="bg-gray-50 dark:bg-gray-800">
          <Group gap="md" className="flex-wrap">
            {/* Year Selection */}
            <Group gap="xs">
              <TbCalendar className="text-gray-500" size={18} />
              <Select
                data={availableYears.map((year) => ({
                  value: year.toString(),
                  label: year.toString(),
                }))}
                value={selectedYear.toString()}
                onChange={(value) => setSelectedYear(Number(value))}
                allowDeselect={false}
                size="sm"
                w={100}
              />
            </Group>

            {/* Layer Selection */}
            <Group gap="xs">
              <BiLayer className="text-gray-500" size={18} />
              <Select
                data={layerOptions}
                value={activeLayer}
                onChange={(value) => handleLayerChange(value as string)}
                allowDeselect={false}
                size="sm"
                w={180}
              />
            </Group>

            {/* Comparison Controls */}
            {viewMode === "compare-years" && availableYears.length > 1 && (
              <Group
                gap="xs"
                className="border-gray-300 pl-4 dark:border-gray-700 border-l"
              >
                <Text size="sm" fw={500} c="dimmed">
                  Compare with:
                </Text>
                <Select
                  data={availableYears
                    .filter((year) => year !== selectedYear)
                    .map((year) => ({
                      value: year.toString(),
                      label: year.toString(),
                    }))}
                  value={compareYear.toString()}
                  onChange={(value) => setCompareYear(Number(value))}
                  allowDeselect={false}
                  size="sm"
                  w={100}
                />
              </Group>
            )}

            {viewMode === "compare-layers" && (
              <Group
                gap="xs"
                className="border-gray-300 pl-4 dark:border-gray-700 border-l"
              >
                <Text size="sm" fw={500} c="dimmed">
                  Compare with:
                </Text>
                <Select
                  value={compareLayer}
                  onChange={(value) =>
                    setCompareLayer(value as typeof compareLayer)
                  }
                  data={[
                    {
                      value: "biomass",
                      label: t("biomassDensity"),
                      disabled:
                        !currentImagery?.biomassUrl ||
                        activeLayer === "biomass",
                    },
                    {
                      value: "biomass-change",
                      label: t("biomassChange"),
                      disabled:
                        !currentImagery?.biomassChangeUrl ||
                        activeLayer === "biomass-change",
                    },
                    {
                      value: "sentinel",
                      label: t("sentinel"),
                      disabled:
                        !currentImagery?.sentinelUrl ||
                        activeLayer === "sentinel",
                    },
                  ]}
                  size="sm"
                  w={180}
                />
              </Group>
            )}
          </Group>
        </Paper>

        {/* Map Area */}
        {currentImagery ? (
          <div className="min-h-[500px]">
            {/* Single View */}
            {viewMode === "single" && (
              <div className="relative h-[500px] overflow-hidden rounded-lg">
                {activeLayer === "biomass" && currentImagery.biomassUrl && (
                  <GeoTiffViewer
                    url={currentImagery.biomassUrl}
                    type="biomass"
                  />
                )}
                {activeLayer === "biomass-change" &&
                  currentImagery.biomassChangeUrl && (
                    <GeoTiffViewer
                      url={currentImagery.biomassChangeUrl}
                      type="biomass-change"
                    />
                  )}
                {activeLayer === "sentinel" && currentImagery.sentinelUrl && (
                  <GeoTiffViewer
                    url={currentImagery.sentinelUrl}
                    type="sentinel"
                  />
                )}

                {/* Legend Overlay */}
                <div className="absolute right-4 bottom-4 z-10 max-w-[200px] rounded bg-white/90 p-2 shadow-lg dark:bg-gray-900/90">
                  {activeLayer === "biomass" && currentImagery.biomassUrl && (
                    <ColorLegend type="viridis" />
                  )}
                  {activeLayer === "biomass-change" &&
                    currentImagery.biomassChangeUrl && (
                      <ColorLegend type="rdylgr" />
                    )}
                </div>
              </div>
            )}

            {/* Compare Years View */}
            {viewMode === "compare-years" &&
              (() => {
                const compareImagery = imagery.find(
                  (img) => img.year === compareYear,
                );
                const leftUrl =
                  activeLayer === "biomass"
                    ? currentImagery.biomassUrl
                    : activeLayer === "biomass-change"
                      ? currentImagery.biomassChangeUrl
                      : currentImagery.sentinelUrl;
                const rightUrl =
                  activeLayer === "biomass"
                    ? compareImagery?.biomassUrl
                    : activeLayer === "biomass-change"
                      ? compareImagery?.biomassChangeUrl
                      : compareImagery?.sentinelUrl;

                if (!leftUrl || !rightUrl) {
                  return (
                    <div className="flex h-[500px] items-center justify-center rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 overflow-hidden">
                      <Text size="sm" c="dimmed">
                        {t("selectYearsWithData")}
                      </Text>
                    </div>
                  );
                }

                return (
                  <div className="relative h-[500px] overflow-hidden rounded-lg">
                    <ImageryComparison
                      leftUrl={leftUrl}
                      rightUrl={rightUrl}
                      leftLabel={`${selectedYear} - ${t(activeLayer === "biomass" ? "biomassDensity" : activeLayer === "biomass-change" ? "biomassChange" : "sentinel")}`}
                      rightLabel={`${compareYear} - ${t(activeLayer === "biomass" ? "biomassDensity" : activeLayer === "biomass-change" ? "biomassChange" : "sentinel")}`}
                      type={activeLayer}
                    />
                  </div>
                );
              })()}

            {/* Compare Layers View */}
            {viewMode === "compare-layers" &&
              (() => {
                const leftUrl =
                  activeLayer === "biomass"
                    ? currentImagery.biomassUrl
                    : activeLayer === "biomass-change"
                      ? currentImagery.biomassChangeUrl
                      : currentImagery.sentinelUrl;
                const rightUrl =
                  compareLayer === "biomass"
                    ? currentImagery.biomassUrl
                    : compareLayer === "biomass-change"
                      ? currentImagery.biomassChangeUrl
                      : currentImagery.sentinelUrl;

                if (!leftUrl || !rightUrl) {
                  return (
                    <div className="flex h-[500px] items-center justify-center rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 overflow-hidden">
                      <Text size="sm" c="dimmed">
                        {t("selectLayersWithData")}
                      </Text>
                    </div>
                  );
                }

                return (
                  <div className="relative h-[500px] overflow-hidden rounded-lg">
                    <ImageryComparison
                      leftUrl={leftUrl}
                      rightUrl={rightUrl}
                      leftLabel={`${selectedYear} - ${t(activeLayer === "biomass" ? "biomassDensity" : activeLayer === "biomass-change" ? "biomassChange" : "sentinel")}`}
                      rightLabel={`${selectedYear} - ${t(compareLayer === "biomass" ? "biomassDensity" : compareLayer === "biomass-change" ? "biomassChange" : "sentinel")}`}
                      type={activeLayer}
                    />
                  </div>
                );
              })()}
          </div>
        ) : (
          <div className="flex h-[500px] w-full items-center justify-center rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
            <Text size="sm" c="dimmed">
              {t("noDataAvailable", { year: selectedYear })}
            </Text>
          </div>
        )}
      </Stack>
    </Paper>
  );
}
